import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Download, FileText, Globe, CheckCircle2, AlertCircle } from 'lucide-react';
import { worldCountries } from '@/data/countries/index';
import { citySpecialties } from '@/data/citySpecialties';
import { generateComprehensiveSitemap, getSitemapStats } from '@/utils/sitemapGenerator';
import { toast } from 'sonner';
import type { Language } from '@/context/LanguageContext';

// All supported languages for the platform
const SUPPORTED_LANGUAGES: Language[] = [
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', // Western
  'da', 'no', 'fi', 'sv', 'is', // Nordic
  'cs', 'sk', 'sl', 'pl', // Central
  'et', 'lv', 'lt', // Baltic
  'el', // Southern
  'ko', 'ja', 'zh', 'hi', 'id', 'th', 'vi', 'ms', 'bn', // Asian
  'ar', 'tr', 'ur', 'fa', // Middle East
  'ru' // Eastern
];

interface SitemapGenerationResult {
  totalURLs: number;
  totalCities: number;
  totalSpecialties: number;
  totalLanguages: number;
  sitemapXML: string;
  sitemapIndex: string;
  robotsTxt: string;
  stats: {
    cityLandings: number;
    specialtySubLandings: number;
    staticPages: number;
  };
}

const CompleteSitemapGenerator: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<SitemapGenerationResult | null>(null);
  const [progress, setProgress] = useState(0);

  // Calculate totals
  const totalCities = worldCountries.reduce((sum, country) => sum + country.topCities.length, 0);
  const totalSpecialties = citySpecialties.length;
  const totalLanguages = 1; // English only for specialty pages
  
  // Calculate realistic totals
  const totalCityLandings = totalCities; // 32,853 city pages (English only)
  const totalSpecialtyLandings = totalCities * totalSpecialties; // 32,853 × 26 = 854,178
  const totalStaticPages = 20; // Approximate number of static pages
  const grandTotal = totalCityLandings + totalSpecialtyLandings + totalStaticPages; // ~887,051

  const generateCompleteSitemap = async () => {
    setIsGenerating(true);
    setProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 90));
      }, 200);

      // Generate sitemap entries
      const baseUrl = 'https://psychotherapist.help';
      const currentDate = new Date().toISOString().split('T')[0];
      
      setProgress(10);

      const entries = await generateComprehensiveSitemap(baseUrl);
      
      clearInterval(progressInterval);
      setProgress(95);

      // Generate sitemap XML
      let sitemapXML = '<?xml version="1.0" encoding="UTF-8"?>\n';
      sitemapXML += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"\n';
      sitemapXML += '        xmlns:xhtml="http://www.w3.org/1999/xhtml">\n';
      
      entries.forEach(entry => {
        sitemapXML += `  <url>\n`;
        sitemapXML += `    <loc>${entry.url}</loc>\n`;
        sitemapXML += `    <lastmod>${entry.lastmod}</lastmod>\n`;
        sitemapXML += `    <changefreq>${entry.changefreq}</changefreq>\n`;
        sitemapXML += `    <priority>${entry.priority}</priority>\n`;
        sitemapXML += `  </url>\n`;
      });
      
      sitemapXML += '</urlset>';

      // Generate sitemap index for multiple sitemaps (Google limit: 50,000 URLs per sitemap)
      const maxUrlsPerSitemap = 45000;
      const numSitemaps = Math.ceil(entries.length / maxUrlsPerSitemap);
      
      let sitemapIndex = '<?xml version="1.0" encoding="UTF-8"?>\n';
      sitemapIndex += '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
      
      for (let i = 0; i < numSitemaps; i++) {
        sitemapIndex += `  <sitemap>\n`;
        sitemapIndex += `    <loc>${baseUrl}/sitemap-${i + 1}.xml</loc>\n`;
        sitemapIndex += `    <lastmod>${currentDate}</lastmod>\n`;
        sitemapIndex += `  </sitemap>\n`;
      }
      
      sitemapIndex += '</sitemapindex>';

      // Generate robots.txt
      const robotsTxt = `# Robots.txt for Psychotherapist.help
# Generated: ${currentDate}

User-agent: *
Allow: /

# Sitemap location
Sitemap: ${baseUrl}/sitemap-index.xml

# Disallow admin and development pages
Disallow: /admin/
Disallow: /dev/
Disallow: /api/

# Crawl-delay for respectful crawling
Crawl-delay: 0.5
`;

      // Calculate stats
      const stats = {
        cityLandings: totalCityLandings,
        specialtySubLandings: totalSpecialtyLandings,
        staticPages: totalStaticPages
      };

      setProgress(100);

      setResult({
        totalURLs: entries.length,
        totalCities,
        totalSpecialties,
        totalLanguages,
        sitemapXML,
        sitemapIndex,
        robotsTxt,
        stats
      });

      toast.success('Sitemap completo generado exitosamente', {
        description: `${entries.length.toLocaleString()} URLs generadas`
      });

    } catch (error) {
      console.error('Error generating sitemap:', error);
      toast.error('Error al generar el sitemap');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`${filename} descargado exitosamente`);
  };

  const downloadAllFiles = async () => {
    if (!result) return;
    
    downloadFile(result.sitemapIndex, 'sitemap-index.xml');
    
    // Split into multiple sitemaps if needed
    const maxUrlsPerSitemap = 50000;
    const entries = await generateComprehensiveSitemap('https://psychotherapist.help');
    const numSitemaps = Math.ceil(entries.length / maxUrlsPerSitemap);
    
    for (let i = 0; i < numSitemaps; i++) {
      const start = i * maxUrlsPerSitemap;
      const end = Math.min((i + 1) * maxUrlsPerSitemap, entries.length);
      const chunk = entries.slice(start, end);
      
      let sitemapXML = '<?xml version="1.0" encoding="UTF-8"?>\n';
      sitemapXML += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
      
      chunk.forEach(entry => {
        sitemapXML += `  <url>\n`;
        sitemapXML += `    <loc>${entry.url}</loc>\n`;
        sitemapXML += `    <lastmod>${entry.lastmod}</lastmod>\n`;
        sitemapXML += `    <changefreq>${entry.changefreq}</changefreq>\n`;
        sitemapXML += `    <priority>${entry.priority}</priority>\n`;
        sitemapXML += `  </url>\n`;
      });
      
      sitemapXML += '</urlset>';
      
      downloadFile(sitemapXML, `sitemap-${i + 1}.xml`);
    }
    
    downloadFile(result.robotsTxt, 'robots.txt');
    
    toast.success('Todos los archivos descargados', {
      description: `${numSitemaps + 2} archivos descargados en total`
    });
  };

  return (
    <>
      <Helmet>
        <title>Generador de Sitemap Completo - 32,853 Ciudades</title>
        <meta name="description" content="Generador de sitemap XML completo para 32,853 ciudades y 850,694 specialty sub-landings" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8 px-4">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <Globe className="h-10 w-10 text-primary" />
              Generador de Sitemap Completo
            </h1>
            <p className="text-muted-foreground text-lg">
              Sistema de generación de sitemap XML para indexación completa en Google
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Ciudades</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{totalCities.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">En {worldCountries.length} países</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">City Landings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{totalCityLandings.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">{totalCities.toLocaleString()} × {totalLanguages} idiomas</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Specialty Sub-Landings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{totalSpecialtyLandings.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">{totalCities.toLocaleString()} × {totalSpecialties} specialties</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total URLs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{grandTotal.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">Todas las páginas</p>
              </CardContent>
            </Card>
          </div>

          {/* Generation Status */}
          {!result && (
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                El sitemap incluirá todas las city landings, specialty sub-landings en {totalLanguages} idiomas, 
                y páginas estáticas. Total estimado: <strong>{grandTotal.toLocaleString()} URLs</strong>.
              </AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                ¡Sitemap generado exitosamente! {result.totalURLs.toLocaleString()} URLs listas para indexación en Google.
              </AlertDescription>
            </Alert>
          )}

          {/* Generation Progress */}
          {isGenerating && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Generando Sitemap...</CardTitle>
                <CardDescription>
                  Procesando {grandTotal.toLocaleString()} URLs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={progress} className="mb-2" />
                <p className="text-sm text-muted-foreground text-center">{progress}% completado</p>
              </CardContent>
            </Card>
          )}

          {/* Generation Control */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Control de Generación</CardTitle>
              <CardDescription>
                Genera el sitemap XML completo para todas las landings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={generateCompleteSitemap}
                disabled={isGenerating}
                size="lg"
                className="w-full"
              >
                <FileText className="mr-2 h-5 w-5" />
                {isGenerating ? 'Generando...' : 'Generar Sitemap Completo'}
              </Button>

              {result && (
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-semibold mb-2">Archivos Generados:</h3>
                  
                  <Button
                    variant="outline"
                    onClick={() => downloadFile(result.sitemapIndex, 'sitemap-index.xml')}
                    className="w-full"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar sitemap-index.xml
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => downloadFile(result.robotsTxt, 'robots.txt')}
                    className="w-full"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar robots.txt
                  </Button>

                  <Button
                    onClick={downloadAllFiles}
                    className="w-full"
                    variant="default"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar Todos los Archivos
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Details */}
          {result && (
            <Card>
              <CardHeader>
                <CardTitle>Detalles del Sitemap</CardTitle>
                <CardDescription>
                  Estadísticas detalladas de las URLs generadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">City Landings:</span>
                    <Badge variant="secondary" className="text-lg">
                      {result.stats.cityLandings.toLocaleString()}
                    </Badge>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">Specialty Sub-Landings:</span>
                    <Badge variant="secondary" className="text-lg">
                      {result.stats.specialtySubLandings.toLocaleString()}
                    </Badge>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">Páginas Estáticas:</span>
                    <Badge variant="secondary" className="text-lg">
                      {result.stats.staticPages}
                    </Badge>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg border-2 border-primary">
                    <span className="font-bold text-lg">Total URLs:</span>
                    <Badge className="text-lg px-4 py-1">
                      {result.totalURLs.toLocaleString()}
                    </Badge>
                  </div>

                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      El sitemap ha sido dividido en múltiples archivos según las limitaciones de Google 
                      (máximo 50,000 URLs por sitemap). Descarga todos los archivos y súbelos a tu servidor.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default CompleteSitemapGenerator;
